import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Landmark, Wallet, CheckCircle2, CreditCard, HandCoins, ChevronRight, X, CalendarDays, AlertCircle,
} from 'lucide-react';
import type { EmployeeLoan, EmployeeAdvance } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { StatTile } from '@/components/ui/stat-tile';
import { AccountSalaryGate } from '@/components/payroll/AccountSalaryGate';
import {
  LOAN_STATUS_COLORS, advanceDisplayStatus, deductionRowStatus, currentMonthKey, monthKey,
} from '@/components/payroll/loan-status';
import { formatINR } from '@/lib/currency';
import { formatDate } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';

function formatMonthYear(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
}

/** Recovered so far = amount minus what the ledger says is still owed. */
function loanFigures(loan: EmployeeLoan) {
  const amount = parseFloat(loan.amount);
  const remaining = Math.max(0, parseFloat(loan.remaining_amount ?? loan.amount));
  const recovered = Math.max(0, amount - remaining);
  return { amount, remaining, recovered, pct: amount > 0 ? Math.min(100, (recovered / amount) * 100) : 0 };
}

const SECTION_LABEL = 'mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground';

/**
 * Read-only view of the employee's own loans and salary advances. HR creates and manages
 * them on HRMS > Payroll > Loans & Advances. Behind the same personal key as My Salary, and
 * for the same reason: nothing is fetched until the employee has unlocked.
 */
export function MyLoansPage() {
  return (
    <AccountSalaryGate
      title="Your loans are locked"
      description="Enter your personal salary pass key to view your loans and advances."
    >
      <MyLoansContent />
    </AccountSalaryGate>
  );
}

function MyLoansContent() {
  const navigate = useNavigate();
  const [openLoan, setOpenLoan] = useState<EmployeeLoan | null>(null);
  const current = currentMonthKey();

  const loansQuery = useQuery({
    queryKey: ['payroll', 'loans', 'me'],
    queryFn: () => payrollService.getMyLoans(),
  });
  const advancesQuery = useQuery({
    queryKey: ['payroll', 'advances', 'me'],
    queryFn: () => payrollService.getMyAdvances(),
  });

  const loans = loansQuery.data ?? [];
  const advances = advancesQuery.data ?? [];

  const openLoans = loans.filter((l) => l.status !== 'closed');
  const outstanding = openLoans.reduce((sum, l) => sum + loanFigures(l).remaining, 0);
  const recovered = loans.reduce((sum, l) => sum + loanFigures(l).recovered, 0);
  const activeCount = loans.filter((l) => l.status === 'active').length;
  // Still to come out of salary: scheduled for this month or later, or not scheduled yet.
  const advancesDue = advances
    .filter((a) => ['This month', 'Upcoming', 'Pending'].includes(advanceDisplayStatus(a, current).label))
    .reduce((sum, a) => sum + parseFloat(a.amount), 0);

  const statsLoading = loansQuery.isLoading || advancesQuery.isLoading;

  return (
    <div className="space-y-4">
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/account')}
          className="rounded-lg p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          aria-label="Back to account"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <Landmark className="h-5 w-5 text-teal-600" />
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Loan Overview</h1>
        </div>
      </div>

      {/* ── Summary ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatTile label="Outstanding" value={statsLoading ? '…' : formatINR(outstanding)} sub="Loans still owed" icon={Wallet} tone="rose" />
        <StatTile label="Recovered" value={statsLoading ? '…' : formatINR(recovered)} sub="Deducted so far" icon={CheckCircle2} tone="emerald" />
        <StatTile label="Active Loans" value={statsLoading ? '…' : activeCount} sub={`${loans.length} in total`} icon={CreditCard} tone="teal" />
        <StatTile label="Advances Due" value={statsLoading ? '…' : formatINR(advancesDue)} sub="Yet to be deducted" icon={HandCoins} tone="amber" />
      </div>

      {/* ── Loans ────────────────────────────────────────────────────────── */}
      <div>
        <p className={SECTION_LABEL}>My Loans</p>
        {loansQuery.isLoading ? (
          <Skeletons />
        ) : loansQuery.isError ? (
          <ErrorCard message={getErrorMessage(loansQuery.error)} onRetry={() => loansQuery.refetch()} />
        ) : loans.length === 0 ? (
          <EmptyCard text="No loans on record" />
        ) : (
          <div className="space-y-2">
            {loans.map((loan) => (
              <LoanRow key={loan.id} loan={loan} onClick={() => setOpenLoan(loan)} />
            ))}
          </div>
        )}
      </div>

      {/* ── Advances ─────────────────────────────────────────────────────── */}
      <div>
        <p className={SECTION_LABEL}>My Salary Advances</p>
        {advancesQuery.isLoading ? (
          <Skeletons />
        ) : advancesQuery.isError ? (
          <ErrorCard message={getErrorMessage(advancesQuery.error)} onRetry={() => advancesQuery.refetch()} />
        ) : advances.length === 0 ? (
          <EmptyCard text="No advances on record" />
        ) : (
          <div className="space-y-2">
            {advances.map((adv) => (
              <AdvanceRow key={adv.id} advance={adv} current={current} />
            ))}
          </div>
        )}
      </div>

      <LoanDrawer loan={openLoan} open={!!openLoan} onClose={() => setOpenLoan(null)} />
    </div>
  );
}

function LoanRow({ loan, onClick }: { loan: EmployeeLoan; onClick: () => void }) {
  const { amount, remaining, pct } = loanFigures(loan);
  const interest = parseFloat(loan.interest_rate);

  return (
    <button
      onClick={onClick}
      className="w-full rounded-lg border border-border/60 bg-card px-3 py-2.5 text-left transition hover:shadow-sm hover:bg-muted/30"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40">
          <CreditCard className="h-4 w-4 text-teal-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{formatINR(amount)}</span>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${LOAN_STATUS_COLORS[loan.status] ?? ''}`}>
              {loan.status}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            EMI {formatINR(loan.emi_amount)} · From {formatDate(loan.start_date)}
            {interest > 0 ? ` · ${interest.toFixed(1)}% interest` : ''}
          </p>
          {loan.reason && <p className="text-[11px] text-muted-foreground truncate">{loan.reason}</p>}
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-muted">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {loan.emis_paid ?? 0}/{loan.num_emis} EMIs
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-sm font-bold ${remaining > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {formatINR(remaining)}
          </p>
          <p className="text-[11px] text-muted-foreground">Remaining</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </button>
  );
}

function AdvanceRow({ advance, current }: { advance: EmployeeAdvance; current: number }) {
  const status = advanceDisplayStatus(advance, current);
  const recoveryMonth = advance.deduction_year && advance.deduction_month
    ? formatMonthYear(advance.deduction_year, advance.deduction_month)
    : 'Not scheduled';

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40">
        <HandCoins className="h-4 w-4 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{formatINR(advance.amount)}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
          Taken {formatDate(advance.advance_date)} · Recovery {recoveryMonth}
        </p>
        {advance.reason && <p className="text-[11px] text-muted-foreground truncate">{advance.reason}</p>}
      </div>
      <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${status.cls}`}>
        {status.label}
      </span>
    </div>
  );
}

function LoanDrawer({ loan, open, onClose }: { loan: EmployeeLoan | null; open: boolean; onClose: () => void }) {
  return (
    <>
      <div
        className={`fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-x-0 bottom-0 z-modal max-h-[88vh] overflow-y-auto rounded-t-2xl bg-card shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {loan && <LoanDrawerContent loan={loan} onClose={onClose} />}
      </div>
    </>
  );
}

function LoanDrawerContent({ loan, onClose }: { loan: EmployeeLoan; onClose: () => void }) {
  const current = currentMonthKey();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['payroll', 'loan-deductions', 'me', loan.id],
    queryFn: () => payrollService.getMyLoanDeductions(loan.id),
  });

  const months = [...(data?.deductions ?? [])].sort(
    (a, b) => monthKey(a.year, a.month) - monthKey(b.year, b.month),
  );
  const unscheduled = data ? data.summary.remaining - data.summary.upcoming : 0;

  return (
    <div>
      <div className="bg-teal-800 px-5 pt-3 pb-3 text-white">
        <div className="mx-auto mb-2.5 h-1 w-10 rounded-full bg-white/20" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="font-semibold">Loan of {formatINR(loan.amount)}</span>
            <span className="text-xs text-teal-200">· from {formatDate(loan.start_date)}</span>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {isLoading ? (
          <Skeletons />
        ) : isError || !data ? (
          <ErrorCard message={getErrorMessage(error)} onRetry={() => refetch()} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label="Total Loan" value={formatINR(loan.amount)} sub={`EMI ${formatINR(loan.emi_amount)}`} icon={CreditCard} tone="teal" />
              <StatTile label="Recovered" value={formatINR(data.summary.recovered)} sub={`${data.summary.installments} month(s)`} icon={CheckCircle2} tone="emerald" />
              <StatTile label="Remaining" value={formatINR(data.summary.remaining)} icon={Wallet} tone="rose" />
              <StatTile label="Scheduled Ahead" value={formatINR(data.summary.upcoming)} sub="This month onwards" icon={CalendarDays} tone="violet" />
            </div>

            {loan.status !== 'closed' && unscheduled > 0.005 && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/40 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {formatINR(unscheduled)} is not scheduled for any month yet. HR will set its deduction months.
              </div>
            )}

            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Month by Month
              </p>
              {months.length === 0 ? (
                <EmptyCard text="No deductions scheduled yet" />
              ) : (
                <div className="divide-y divide-border/50 rounded-lg border border-border/60">
                  {months.map((row) => {
                    const status = deductionRowStatus(row, current);
                    return (
                      <div key={`${row.year}-${row.month}`} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="text-sm font-medium">{formatMonthYear(row.year, row.month)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{formatINR(row.amount)}</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${status.cls}`}>
                            {status.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Skeletons() {
  return (
    <div className="space-y-2">
      {[1, 2].map((i) => (
        <div key={i} className="h-16 rounded-lg bg-slate-100 dark:bg-muted animate-pulse" />
      ))}
    </div>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border/60 bg-card px-4 py-6 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400">
      <span className="min-w-0">{message}</span>
      <button onClick={onRetry} className="shrink-0 font-medium underline underline-offset-2">
        Retry
      </button>
    </div>
  );
}
